
import React from 'react';
import * as Icons from 'lucide-react';
import { formatCurrencyCOP } from '../utils/formatters';

const PaymentVerificationModal = ({
    isOpen,
    onClose,
    onConfirm,
    orderTotal,
    totalPaid, // This is the amount currently being registered + previously paid? No, usually just the amount registered compared to what's pending.
    // Actually, let's keep it simple: Pass 'remaining' and 'totalRegistered'.
    remaining,
    totalRegistered
}) => {
    if (!isOpen) return null;

    const isOverpayment = remaining < 0;
    const isPartialPayment = remaining > 0;
    const difference = Math.abs(remaining);

    // Configuration based on type
    const config = isOverpayment ? {
        icon: Icons.AlertTriangle,
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        title: 'Confirmar Sobrepago',
        borderColor: 'border-yellow-200',
        headerBg: 'bg-red-50',
        headerText: 'text-red-700',
        headerBorder: 'border-red-200',
        headerLabel: 'ADVERTENCIA',
        headerMessage: 'El valor ingresado es mayor al valor pendiente.',
        diffLabel: 'Exceso (Saldo a Favor)',
        diffColor: 'text-yellow-700',
        confirmBtnBg: 'bg-yellow-600',
        confirmBtnHover: 'hover:bg-yellow-700',
        confirmBtnRing: 'focus:ring-yellow-500',
        confirmText: 'Sí, Aprobar y Crear RC',
        question: '¿El cliente tiene conocimiento de este saldo a favor?',
        note: `Al confirmar, se creará el Recibo de Caja por el valor total transferido (${formatCurrencyCOP(totalRegistered)}). El saldo a favor quedará registrado en SIIGO automáticamente.`
    } : {
        // Partial Payment (Underpayment)
        icon: Icons.Info,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        title: 'Confirmar Pago Parcial',
        borderColor: 'border-blue-200',
        headerBg: 'bg-blue-50',
        headerText: 'text-blue-700',
        headerBorder: 'border-blue-200',
        headerLabel: 'INFORMACIÓN',
        headerMessage: 'El valor ingresado es menor al valor pendiente.',
        diffLabel: 'Faltante (Por Cobrar)',
        diffColor: 'text-red-600',
        confirmBtnBg: 'bg-blue-600',
        confirmBtnHover: 'hover:bg-blue-700',
        confirmBtnRing: 'focus:ring-blue-500',
        confirmText: 'Sí, Registrar Parcial',
        question: '¿Deseas registrar este pago como parcial?',
        note: `El pedido quedará con un saldo pendiente de ${formatCurrencyCOP(difference)}.`
    };

    return (
        <div className="fixed inset-0 z-[70] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full animate-in fade-in zoom-in duration-200">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.iconBg} sm:mx-0 sm:h-10 sm:w-10`}>
                                <config.icon className={`h-6 w-6 ${config.iconColor}`} aria-hidden="true" />
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    {config.title}
                                </h3>
                                <div className="mt-2 text-sm text-gray-500 space-y-3">
                                    <div className={`${config.headerBg} p-2 ${config.headerText} text-xs rounded border ${config.headerBorder}`}>
                                        <strong>{config.headerLabel}:</strong> {config.headerMessage}
                                    </div>

                                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                        <div className="flex justify-between">
                                            <span>Valor Pendiente (Esperado):</span>
                                            {/* logic: expected = registered + remaining. 
                          If overpayment (-100 remaining), expected = 1000 + (-100) = 900. Correct.
                          If underpayment (+100 remaining), expected = 900 + 100 = 1000. Correct.
                      */}
                                            <span className="font-bold">{formatCurrencyCOP(totalRegistered + remaining)}</span>
                                        </div>
                                        <div className="flex justify-between mt-1">
                                            <span>Valor a Registrar:</span>
                                            <span className="font-bold text-blue-600">{formatCurrencyCOP(totalRegistered)}</span>
                                        </div>
                                        <div className={`flex justify-between mt-2 pt-2 border-t border-gray-300 font-bold ${config.diffColor}`}>
                                            <span>{config.diffLabel}:</span>
                                            <span>{isOverpayment ? '+' : ''}{formatCurrencyCOP(difference)}</span>
                                        </div>
                                    </div>

                                    <p className="font-bold text-gray-800">
                                        {config.question}
                                    </p>
                                    <p className="text-xs">
                                        {config.note}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={onConfirm}
                            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${config.confirmBtnBg} text-base font-medium text-white ${config.confirmBtnHover} focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.confirmBtnRing} sm:ml-3 sm:w-auto sm:text-sm`}
                        >
                            {config.confirmText}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentVerificationModal;
